import React, { useEffect, useState } from 'react';
import { useTriConnection } from './hooks';
import { setOpenToast } from '../../../../store/helpers/helperReducers';
import { useStoreDispatch } from '../../../../store/hooks';
import { useConfigurePrimaryMutation } from '../../../../store/api/admin/adminApiSlice';
import { Button } from '../../../ui/button';
import { Select } from '../../../ui/select';
import { Label } from '../../../ui/label';
import { InputField } from '../../../Shared/Radix/Inputs/InputField';

interface ISelectOption { text: string; value: string; }

const HearbeatSetting = () => {
  const {  data, isLoading, error,} = useTriConnection();
  const [configurePrimary, { isLoading: saving, error: saveError }] = useConfigurePrimaryMutation();
  const [selectOptions, setSelectOptions] = useState<ISelectOption[]>([]);
  const [isModified, setIsModified] = useState(false);
  const dispatch = useStoreDispatch();

  const [formValues, setFormValues] = useState({
    heartbeat_freqency: 0,
    missed_heartbeat_threshold: 0,
    secondary_username: '',
    secondary_password: '',
  });

  useEffect(() => {
    if (data) {
      setFormValues({
        heartbeat_freqency: data?.heartbeat_freqency,
        missed_heartbeat_threshold: data?.missed_heartbeat_threshold,
        secondary_username: '',
        secondary_password: '',
      });

      const freqencyOptions: ISelectOption[] = [5, 10, 15, 20, 25, 30].map((freq) => ({
        text: `${freq} seconds`,
        value: freq.toString(),
      }));

      setSelectOptions(freqencyOptions);
    }
  }, [data]);

  const checkIfModified = (values: { heartbeat_freqency: any; missed_heartbeat_threshold: any; secondary_username: string; secondary_password: string; }) => {
    return (
      values.heartbeat_freqency !== data?.heartbeat_freqency ||
      values.missed_heartbeat_threshold !== data?.missed_heartbeat_threshold ||
      values.secondary_username !== '' ||
      values.secondary_password !== ''
    );
  };

  const handleInputFieldChange = (name: string, value: string) => {
    setFormValues((prevState) => {
      const updatedValues = { ...prevState, [name]: value };
      setIsModified(checkIfModified(updatedValues));
      return updatedValues;
    });
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues((prevState) => {
      const updatedValues = { ...prevState, [name]: value };
      setIsModified(checkIfModified(updatedValues));
      return updatedValues;
    });
  };

  const handleCancel = () => {
    setFormValues({
      heartbeat_freqency: data?.heartbeat_freqency || 0,
      missed_heartbeat_threshold: data?.missed_heartbeat_threshold || 0,
      secondary_username: '',
      secondary_password: '',
    });
    setIsModified(false);
  };

  const handleSave = async () => {
    try {
      const updatedConfig = {
        ...data,
        heartbeat_freqency: formValues.heartbeat_freqency,
        missed_heartbeat_threshold: formValues.missed_heartbeat_threshold,
        secondary_username: formValues.secondary_username,
        secondary_password: formValues.secondary_password,
        primary_address: data?.primary_address || '', 
        secondary_address: data?.secondary_address || '',
        observer_address: data?.observer_address || '',
      };

      await configurePrimary(updatedConfig).unwrap();
      setIsModified(false);
      dispatch(setOpenToast({ openToast: true, message: "Settings saved successfully", toastType: "success" }));
    } catch (err:any) {
      dispatch(setOpenToast({ openToast: true, message: err?.data, toastType: "error" }));
    }
  };

  useEffect(() => {
    setIsModified(checkIfModified(formValues));
  }, [formValues, data]);

  return (
    <div>
      <div>
        <h6 className="text-base font-semibold text-foreground">Heartbeat Settings</h6>
      </div>

      <div className="mt-7">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="heartbeat_freqency" className="text-sm font-medium">
            Heartbeat Frequency in seconds
          </Label>
          <Select
            id="heartbeat_freqency"
            name="heartbeat_freqency"
            value={formValues.heartbeat_freqency.toString()}
            options={selectOptions.map((opt) => ({ value: opt.value, label: opt.text }))}
            onChange={handleSelectChange}
          />
        </div>
      </div>

      <div className="mt-7">
        <InputField
          label="Missed Heartbeat Threshold"
          name="missed_heartbeat_threshold"
          value={formValues.missed_heartbeat_threshold}
          onChange={handleInputFieldChange}
          required
        />
      </div>

      <div className="mt-7 flex justify-end items-end">
        <div className="mr-7">
          <Button variant="outline" onClick={handleCancel} disabled={!isModified}>Cancel</Button>
        </div>
        <div>
          <Button onClick={handleSave} disabled={!isModified || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HearbeatSetting;